
import { Component, Mixins } from "vue-property-decorator";
import mixin from "./mixin";
import moment from "moment";
import NoteFields from "@/components/widgets/NoteFields.vue";
import notifications from "../../../lib/notifications";
import { getModule } from "vuex-module-decorators";
import { OrderModule } from "@/store/modules/index";
import { CompleteOrderRequest, NewOrderNote } from "../../../models/api/orders/complete";
import * as modals from "../../../lib/modals";

const orderModule = getModule(OrderModule);

const defaultOrder = {
  orderHeaderNumber: null,
  shipDate: null,
  receiveDate: null,
  receiveTime: null,
  loadDate: null,
  loadTime: null,
  completionDate: null,
  completionTime: null,
  note: {
    orderHeaderNumber: null,
    text: null,
    facilityViewable: true,
    customerViewable: true
  },
  carrier: null,
  deliveryForId: 0,
};

@Component({
  name: modals.orderCompletionModal,
  components: {
    NoteFields
  }
})
export default class OrderCompletionModal extends Mixins(mixin) {
  order: CompleteOrderRequest = defaultOrder;
  loading: boolean = false;
  saving: boolean = false;
  request: CompleteOrderRequest = defaultOrder;
  facilityCode: string = null;
  isShipDateOutOfOrder: boolean = false;
  isReceiveDateOutOfOrder: boolean = false;
  isLoadDateOutOfOrder: boolean = false;
  isCompletionDateOutOfOrder: boolean = false;
  outOfOrderWarnings: Array<String> = [];
  allowFutureOrdersForDeliveryType: boolean = false;

  get outOfOrder() {
    this.outOfOrderWarnings = [];
    const { receiveTime, loadTime, completionTime } = this.order;
    let receiveDate = this.order.receiveDate
      ? `${(this.order.receiveDate as string).split("T")[0]} ${receiveTime}`
      : null;
    let loadDate = this.order.loadDate
      ? `${(this.order.loadDate as string).split("T")[0]} ${loadTime}`
      : null;
    let completionDate = this.order.completionDate
      ? `${(this.order.completionDate as string).split("T")[0]} ${completionTime}`
      : null;
    this.isShipDateOutOfOrder = false;
    this.isReceiveDateOutOfOrder = false;
    this.isLoadDateOutOfOrder = false;
    this.isCompletionDateOutOfOrder = false;
    if (!this.order.shipDate) {
      this.isShipDateOutOfOrder = true;
    }
    if (!loadDate || loadTime == "") {
      this.isLoadDateOutOfOrder = true;
    }

    if (!completionDate || completionTime == "") {
      this.isCompletionDateOutOfOrder = true;
    }

    if (!receiveDate || receiveTime == "") {
      this.isReceiveDateOutOfOrder = true;
    }

    if (  this.order.shipDate!=null && (
      moment(this.order.shipDate).isBefore(moment("1/1/1753")) ||
      moment(this.order.shipDate).isAfter(moment("12/31/9999"))
    )) {
      this.isShipDateOutOfOrder = true;
      this.outOfOrderWarnings.push("Ship Date is not a valid date.");
    }

    if (
      this.isShipDateOutOfOrder ||
      this.isReceiveDateOutOfOrder ||
      this.isLoadDateOutOfOrder ||
      this.isCompletionDateOutOfOrder
    )
      return true;
    if (moment(this.order.shipDate).isAfter(moment(receiveDate))) {
      this.isShipDateOutOfOrder = true;
      this.outOfOrderWarnings.push("Ship Date cannot be after Receive Date.");
    }
    if (moment(receiveDate).isAfter(moment(loadDate))) {
      this.isReceiveDateOutOfOrder = true;
      this.outOfOrderWarnings.push("Receive Date cannot be after Load Date.");
    }
    if (moment(loadDate).isAfter(moment(completionDate))) {
      this.isLoadDateOutOfOrder = true;
      this.isCompletionDateOutOfOrder = true;
      this.outOfOrderWarnings.push("Load Date cannot be after Delivery Date.");
      this.outOfOrderWarnings.push("Delivery Date cannot be before Load Date.");
    }
    if (moment(completionDate).isAfter(moment()) && !this.allowFutureOrdersForDeliveryType) {
      this.isCompletionDateOutOfOrder = true;
      this.outOfOrderWarnings.push("Delivery Date cannot be after the Current Date.");
    }
    if (!this.order.carrier || this.order.carrier == "") {
      this.outOfOrderWarnings.push("Carrier can not be blank.");
    }
    return this.outOfOrderWarnings.length != 0;
  }

  reset() {
    this.order = defaultOrder;
    this.loading = false;
    this.saving = false;
  }

  async load() {
    this.show();
    this.loading = true;
    const order = await orderModule.previewCompleteOrder(this.selectedItems[0].number);
    this.order.orderHeaderNumber = order.orderHeaderNumber;
    this.order.note.orderHeaderNumber = order.orderHeaderNumber;
    this.order.shipDate = order.shipDate;
    this.order.receiveDate = order.receiveDate;
    this.order.loadDate = order.loadDate;
    this.order.deliveryForId = order.deliveryForId;
    this.order.completionDate =
      order.completionDate || orderModule.currentOrder.scheduledDeliveryDate;
    this.order.carrier = order.carrier;
    this.facilityCode = order.facilityCode;
    this.allowFutureOrdersForDeliveryType = await orderModule.allowFutureOrdersDeliveryType(order.deliveryType)
      .then((response) => {
        return response.allowFutureOrdersForDeliveryType;
      })
      .catch(() => {
        return false;
      });
    if (order.receiveDate) this.order.receiveTime = moment(order.receiveDate).format("HH:mm");
    if (order.loadDate) this.order.loadTime = moment(order.loadDate).format("HH:mm");
    if (this.order.completionDate)
      this.order.completionTime = moment(this.order.completionDate).format("HH:mm");

    this.loading = false;
  }
  get deliveryForId() {
    if(!this.order)
      return 0;
      
    return this.order.deliveryForId ?? 0;
  }

  async saveModal() {
    this.saving = true;
    this.request = this.order;
    try {
      await orderModule.completeOrder(this.request);
      notifications.success("Order Complete", "Order successfully completed");
      this.reset();
      this.hideModal();
    } catch (ex) {
      notifications.error("Order Complete", "Failed to complete order", ex);
    }
    await this.refreshOrders();
    this.saving = false;
  }

  noteUpdated(newNote) {
    this.order.note = JSON.parse(JSON.stringify(newNote));
  }
}
